/*
 * Copyright (C) 2020 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import errorShipUrl from '@instructure/platform-images/assets/ErrorShip.svg'
import {GenericErrorPage} from '@instructure/platform-generic-error-page'
import {reportError, canvasErrorPageTranslations} from '@canvas/error-page-utils'
import {useScope as createI18nScope} from '@canvas/i18n'
import {LoadingIndicator} from '@instructure/platform-loading-indicator'
import React from 'react'
import {useQuery} from '@apollo/client'
import {INTERNAL_SETTINGS_QUERY} from './graphql/Queries'
import type {InternalSettingsData} from './types'
import {InternalSettingsManager} from './InternalSettingsManager'

const I18n = createI18nScope('internal-settings')

export const InternalSettingsQuery = () => {
  const {loading, error, data} = useQuery<InternalSettingsData>(INTERNAL_SETTINGS_QUERY)

  if (loading) return <LoadingIndicator />
  if (error)
    return (
      <GenericErrorPage
        imageUrl={errorShipUrl}
        onReportError={reportError}
        translations={canvasErrorPageTranslations}
        errorSubject={I18n.t('Internal Settings initial query error')}
        errorCategory={I18n.t('Internal Settings Error Page')}
      />
    )

  return (
    <>
      {data?.internalSettings && (
        <InternalSettingsManager internalSettings={data.internalSettings} />
      )}
    </>
  )
}
